class CollectionDescription extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.toggle = null;
    this.showMoreText = null;
    this.showLessText = null;
    this.banner = null;
    this._inlineFilterButton = null;
    this._facetSticky = null;
    this._facetStickyButton = null;
    this._collectionSection = null;
    this._handleToggleBound = null;
    this._handleInlineFilterClickBound = null;
    this._syncMobileFilterTriggerBound = null;
    this._toggleVisibilityObserver = null;
    this._collectionVisibilityObserver = null;
    this._toggleVisible = null;
    this._collectionEnteredViewport = null;
    this._boxResizeObserver = null;
    this._naturalMetrics = null;
    this._naturalMetricsWidth = null;
  }

  connectedCallback() {
    this.content = this.querySelector('.collection-description-content');
    this.toggle = this.querySelector('.collection-description-toggle');

    if (!this.content || !this.toggle) return;

    this.showMoreText = this.toggle.querySelector('.show-more-text');
    this.showLessText = this.toggle.querySelector('.show-less-text');
    this.banner = this.closest('.banner');
    this._handleToggleBound = this._handleToggle.bind(this);
    this._handleInlineFilterClickBound = this._handleInlineFilterClick.bind(this);
    this._syncMobileFilterTriggerBound = this._handleViewportChange.bind(this);
    this._fitBannerToBoxBound = this._fitBannerToBox.bind(this);

    window.addEventListener('resize', this._fitBannerToBoxBound);

    if (window.requestAnimationFrame) {
      requestAnimationFrame(() => this._init());
    } else {
      setTimeout(() => this._init(), 100);
    }
  }

  disconnectedCallback() {
    if (this.toggle && this._handleToggleBound) {
      this.toggle.removeEventListener('click', this._handleToggleBound);
    }

    window.removeEventListener('resize', this._syncMobileFilterTriggerBound);
    window.removeEventListener('resize', this._fitBannerToBoxBound);
    this._boxResizeObserver?.disconnect();
    this._boxResizeObserver = null;
    this._disconnectVisibilityObservers();

    if (this._inlineFilterButton && this._handleInlineFilterClickBound) {
      this._inlineFilterButton.removeEventListener('click', this._handleInlineFilterClickBound);
    }

    this._setFacetStickyHidden(false);
    this._handleToggleBound = null;
    this._handleInlineFilterClickBound = null;
    this._syncMobileFilterTriggerBound = null;
    this._fitBannerToBoxBound = null;
    this._inlineFilterButton = null;
  }

  _init() {
    if (this._checkIfOverflow()) {
      this.toggle.classList.remove('hidden');
      this.toggle.addEventListener('click', this._handleToggleBound);
      this._setupMobileFilterTrigger();
      this._setupVisibilityObservers();
      this._syncMobileFilterTrigger();
      window.addEventListener('resize', this._syncMobileFilterTriggerBound);
    } else {
      this.toggle.classList.add('hidden');
      this._setFacetStickyHidden(false);
    }

    this._fitBannerToBox();
    this._observeBoxResize();
  }

  _observeBoxResize() {
    if (!this.banner || !('ResizeObserver' in window)) return;

    const box = this.banner.querySelector('.banner__box');
    if (!box) return;

    // Re-fit whenever the box's own content size actually changes for any
    // reason (fade-in animation settling, webfont swap, image load,
    // expand/collapse) instead of trusting a single measurement taken at
    // click/init time, which can be stale if something else is still
    // mid-transition at that exact instant.
    this._boxResizeObserver = new ResizeObserver(() => this._fitBannerToBox());
    this._boxResizeObserver.observe(box);
  }

  _setupMobileFilterTrigger() {
    this._facetSticky = document.querySelector('facet-sticky');
    this._facetStickyButton = this._facetSticky?.querySelector(':scope > button');
    this._collectionSection = document.querySelector('.collection-section');

    if (!this._facetStickyButton || this._inlineFilterButton) return;

    this._inlineFilterButton = document.createElement('button');
    this._inlineFilterButton.type = 'button';
    this._inlineFilterButton.className = `${this._facetStickyButton.className} collection-description-inline-filter`;
    this._inlineFilterButton.innerHTML = this._facetStickyButton.innerHTML;
    this._inlineFilterButton.setAttribute('aria-controls', this._facetStickyButton.getAttribute('aria-controls') || 'FacetDrawerSticky');
    this._inlineFilterButton.setAttribute('aria-expanded', this._facetStickyButton.getAttribute('aria-expanded') || 'false');
    this._inlineFilterButton.hidden = true; 
    this._inlineFilterButton.addEventListener('click', this._handleInlineFilterClickBound);
    this.toggle.insertAdjacentElement('afterend', this._inlineFilterButton);
  }

  _setupVisibilityObservers() {
    this._disconnectVisibilityObservers();
    this._toggleVisible = this._measureToggleVisible();
    this._collectionEnteredViewport = this._measureCollectionEnteredViewport();

    if (!('IntersectionObserver' in window)) return;

    this._toggleVisibilityObserver = new IntersectionObserver((entries) => {
      this._toggleVisible = entries.some((entry) => entry.isIntersecting);
      this._syncMobileFilterTrigger();
    });
    this._toggleVisibilityObserver.observe(this.toggle);

    if (this._collectionSection) {
      this._collectionVisibilityObserver = new IntersectionObserver((entries) => {
        this._collectionEnteredViewport = entries.some((entry) => entry.isIntersecting);
        this._syncMobileFilterTrigger();
      }, {
        rootMargin: '0px 0px -120px 0px'
      });
      this._collectionVisibilityObserver.observe(this._collectionSection);
    }
  }

  _disconnectVisibilityObservers() {
    this._toggleVisibilityObserver?.disconnect();
    this._collectionVisibilityObserver?.disconnect();
    this._toggleVisibilityObserver = null;
    this._collectionVisibilityObserver = null;
  }

  _handleInlineFilterClick(event) {
    event.preventDefault();

    if (!this._facetStickyButton) return;

    this._facetStickyButton.click();
    this._inlineFilterButton?.setAttribute(
      'aria-expanded',
      this._facetStickyButton.getAttribute('aria-expanded') || 'true'
    );
  }

  _checkIfOverflow() {
    const originalMaxHeight = this.content.style.maxHeight;
    const originalOverflow = this.content.style.overflow;

    this.content.style.maxHeight = 'none';
    this.content.style.overflow = 'visible';

    const actualHeight = this.content.scrollHeight;
    const collapsedHeight = this._getCollapsedHeight();

    this.content.style.maxHeight = originalMaxHeight;
    this.content.style.overflow = originalOverflow;

    return actualHeight > collapsedHeight;
  }

  _getCollapsedHeight() {
    const fontSize = parseFloat(window.getComputedStyle(this.content).fontSize);
    return fontSize * 6;
  }

  _isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  _isToggleVisible() {
    if (this._toggleVisible !== null) return this._toggleVisible;
    return this._measureToggleVisible();
  }

  _measureToggleVisible() {
    if (!this.toggle) return false;

    const toggleRect = this.toggle.getBoundingClientRect();
    return toggleRect.bottom > 0 && toggleRect.top < window.innerHeight;
  }

  _hasCollectionEnteredViewport() {
    if (this._collectionEnteredViewport !== null) return this._collectionEnteredViewport;
    return this._measureCollectionEnteredViewport();
  }

  _measureCollectionEnteredViewport() {
    if (!this._collectionSection) return true;

    const collectionRect = this._collectionSection.getBoundingClientRect();
    return collectionRect.top <= window.innerHeight - 120;
  }

  _setFacetStickyHidden(hidden) {
    if (!this._facetSticky) return;

    if (hidden) {
      this._facetSticky.dataset.mobileFilterState = 'hidden';
    } else {
      delete this._facetSticky.dataset.mobileFilterState;
    }
  }

  _handleViewportChange() {
    this._toggleVisible = this._measureToggleVisible();
    this._collectionEnteredViewport = this._measureCollectionEnteredViewport();
    this._syncMobileFilterTrigger();
  }

  _syncMobileFilterTrigger() {
    if (!this._inlineFilterButton || !this._facetStickyButton) return;

    const isExpanded = this.classList.contains('expanded');
    const shouldUseInlineTrigger = this._isMobileViewport() && isExpanded && this._isToggleVisible();
    const shouldHideStickyTrigger = this._isMobileViewport() && isExpanded && (!this._hasCollectionEnteredViewport() || shouldUseInlineTrigger);

    this.classList.toggle('inline-filter-active', shouldUseInlineTrigger);
    this._inlineFilterButton.hidden = !shouldUseInlineTrigger;
    this._inlineFilterButton.setAttribute('aria-hidden', shouldUseInlineTrigger ? 'false' : 'true');
    this._inlineFilterButton.tabIndex = shouldUseInlineTrigger ? 0 : -1;
    this._inlineFilterButton.setAttribute(
      'aria-expanded',
      this._facetStickyButton.getAttribute('aria-expanded') || 'false'
    );
    this._setFacetStickyHidden(shouldHideStickyTrigger);
  }

  _handleToggle() {
    const isExpanded = this.classList.contains('expanded');

    if (isExpanded) {
      this.classList.remove('expanded');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.showMoreText.classList.remove('hidden');
      this.showLessText.classList.add('hidden');
      // Shrink the banner back down before computing where ".collection"
      // starts — otherwise the description text has already collapsed
      // (shorter) while the banner is still inflated from before (taller),
      // an inconsistent in-between layout that throws off which direction
      // (and how far) _scrollToCollectionStart needs to move.
      this._fitBannerToBox();
      this._scrollToCollectionStart();
    } else {
      this.classList.add('expanded');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.showMoreText.classList.add('hidden');
      this.showLessText.classList.remove('hidden');
      this._fitBannerToBox();
    }

    requestAnimationFrame(() => {
      this._fitBannerToBox();
      this._syncMobileFilterTrigger();
    });
  }

  // Collapsing can shrink the page by thousands of px (the banner shrinking
  // back down on top of the description collapsing). If the user had
  // scrolled deep into the expanded content, the browser clamps their now
  // out-of-bounds scroll position to whatever the new bottom of the page is
  // — on a short collapsed page that can land them in the footer. Scrolling
  // to the collection grid instead gives a sane landing spot, on both mobile
  // and desktop (this used to be mobile-only, back when only mobile grew
  // tall enough on expand for this to matter).
  _scrollToCollectionStart() {
    if (!this._collectionSection) return;

    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 95;
    const top = this._collectionSection.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({
      top,
      behavior: 'auto'
    });
  }

  // Banner's natural (CSS-only, uninflated) height and — on mobile — the box's
  // native bottom% resolved to px. Measured off a throwaway clone so it never
  // touches the live banner's applied height: writing '' to the live element
  // to "peek" at its natural size, even for one synchronous instant, briefly
  // collapses a banner that can be thousands of px tall on an expanded
  // description. If the user is scrolled deep into that content when a
  // ResizeObserver-triggered refit does this (e.g. the mobile inline-filter
  // button toggling visibility), the browser corrects the scroll position for
  // the collapse and then doesn't undo that correction when the height is
  // reapplied a moment later, leaving the page jumping/jerking mid-scroll.
  _getNaturalBannerMetrics() {
    if (this._naturalMetrics && this._naturalMetricsWidth === window.innerWidth) {
      return this._naturalMetrics;
    }

    const clone = this.banner.cloneNode(true);
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.position = 'absolute';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.right = '0';
    clone.style.width = getComputedStyle(this.banner).width;
    clone.style.height = '';
    const cloneBox = clone.querySelector('.banner__box');
    if (cloneBox) cloneBox.style.bottom = '';

    // Force the clone back to the true collapsed/natural state regardless of
    // whatever state the live element is currently in (expanded, inline
    // filter button showing), so this is safe to (re)measure at any time.
    const cloneDescription = clone.querySelector('collection-description');
    cloneDescription?.classList.remove('expanded');
    clone.querySelector('.collection-description-inline-filter')?.remove();

    this.banner.insertAdjacentElement('beforebegin', clone);
    this._naturalMetrics = {
      height: clone.getBoundingClientRect().height,
      boxBottomPx: cloneBox ? (parseFloat(getComputedStyle(cloneBox).bottom) || 0) : 0,
      boxHeight: cloneBox ? cloneBox.getBoundingClientRect().height : 0,
    };
    clone.remove();

    this._naturalMetricsWidth = window.innerWidth;
    return this._naturalMetrics;
  }

  // Grows the fixed-height banner so title/description/toggle never get
  // clipped by its overflow:hidden — by exactly however much the box has
  // grown past its natural (collapsed, un-inflated) height. Growing by that
  // exact delta keeps the box's top pinned at wherever it naturally sat
  // to begin with: it can never end up clipped by the banner (which only
  // happens when the box grows) and can never end up overlapping the header
  // above it either (which only happens if it's pushed higher than natural).
  _fitBannerToBox() {
    if (!this.banner) return;

    const box = this.banner.querySelector('.banner__box');
    if (!box) return;

    const { height: naturalHeight, boxBottomPx: naturalBoxBottomPx, boxHeight: naturalBoxHeight } = this._getNaturalBannerMetrics();

    // The inline filter button toggles visible/hidden based on scroll
    // position (see _syncMobileFilterTrigger), independent of the
    // title/description content this function is fitting the banner to.
    // Excluding its height keeps the fit stable while the user scrolls
    // instead of growing/shrinking the banner (and shifting the page under
    // them) every time that button flips visibility.
    const inlineFilterHeight = (this._inlineFilterButton && !this._inlineFilterButton.hidden)
      ? this._inlineFilterButton.getBoundingClientRect().height + (parseFloat(getComputedStyle(this._inlineFilterButton).marginTop) || 0)
      : 0;
    const boxHeight = box.getBoundingClientRect().height - inlineFilterHeight;
    const growth = Math.max(0, Math.ceil(boxHeight - naturalBoxHeight));

    if (this._isMobileViewport()) {
      // Mobile positions the box with position:absolute + bottom:-47% (see
      // main-collection-banner.liquid) so it can overlap the hero image.
      // That bottom is a % of the banner's height, so if the box grows
      // taller (expanded description, long title) while bottom stays a %,
      // the box's TOP shoots up past the header instead of the box growing
      // downward. Freeze bottom to its natural px value so growth below
      // extends the banner without shifting the box's top at all.
      box.style.bottom = naturalBoxBottomPx + 'px';
    } else {
      box.style.bottom = '';
    }

    this.banner.style.height = growth > 0 ? (naturalHeight + growth) + 'px' : '';
  }
}

customElements.define('collection-description', CollectionDescription);
